import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { post, patch } from "../../lib/request"
import { toPayloadEventId } from "../../lib/utils/eventRecordId"
import { buildPostsApiPatchPayload } from "../../lib/utils/postPatchPayload"

// Content post type constants
export const CONTENT_POST_TYPES = {
  PHOTO: 1,
  REEL: 2,
  VIDEO: 3,
  STORY: 4,
  TEXT: 5,
}

// Async thunk for creating campaign
export const createCampaign = createAsyncThunk(
  "campaign/create",
  async ({ clientId, campaignData }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!clientId) {
        throw new Error("Client ID is required")
      }

      const campaignPayload = { ...campaignData }
      const normalizedEventId = toPayloadEventId(campaignPayload.eventId)
      if (normalizedEventId) {
        campaignPayload.eventId = normalizedEventId
      } else {
        delete campaignPayload.eventId
      }

      const data = await post(`topics/clients/${clientId}`, campaignPayload)

      return data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create campaign")
    }
  },
)

export const updatePost = createAsyncThunk(
  "campaign/updatePost",
  async ({ postId, postData }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!postId) {
        throw new Error("Post ID is required")
      }

      // Only send fields that are provided and not blank
      const payload = {}

      /** Allow JSON null so clients can clear associations (e.g. deselect optional event on a post). */
      const explicitNullKeys = new Set(["eventId", "event"])

      Object.keys(postData).forEach((key) => {
        const value = postData[key]

        if (explicitNullKeys.has(key) && value === null) {
          payload[key] = null
          return
        }

        // Skip undefined, null, and empty strings
        if (value === undefined || value === null || value === "") {
          return
        }

        // Skip empty arrays (allow payload: [] to clear image tags on post)
        if (
          Array.isArray(value) &&
          value.length === 0 &&
          key !== "payload" &&
          key !== "mediaUserTags"
        ) {
          return
        }

        // Skip empty objects (except brandToneScore which can have nested values)
        if (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) {
          return
        }

        payload[key] = value
      })

      const apiPayload = buildPostsApiPatchPayload(payload)

      if (apiPayload.mediaUserTags != null) {
        console.log("[updatePost] posts/" + postId, {
          mediaUserTagsLength: Array.isArray(apiPayload.mediaUserTags)
            ? apiPayload.mediaUserTags.length
            : Array.isArray(apiPayload.posts?.[0]?.mediaUserTags)
              ? apiPayload.posts[0].mediaUserTags.length
              : 0,
        })
      }

      const data = await patch(`posts/${postId}`, apiPayload)

      return { postId, data }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update post")
    }
  },
)

const initialState = {
  campaigns: [],
  currentCampaign: null,
  status: "idle", // idle | loading | succeeded | failed
  error: null,
  publishSettings: {
    postToFeed: true,
    platformSpecific: false,
    selectedPlatforms: [],
  },
  updateStatus: "idle",
  updateError: null,
}

const campaignSlice = createSlice({
  name: "campaign",
  initialState,
  reducers: {
    clearCampaigns: (state) => {
      state.campaigns = []
      state.currentCampaign = null
      state.status = "idle"
      state.error = null
    },
    clearCampaignError: (state) => {
      state.error = null
    },
    setCurrentCampaign: (state, action) => {
      state.currentCampaign = action.payload
    },
    updatePublishSettings: (state, action) => {
      state.publishSettings = { ...state.publishSettings, ...action.payload }
    },
    resetCampaignOnLogout: (state) => {
      state.campaigns = []
      state.currentCampaign = null
      state.status = "idle"
      state.error = null
      state.publishSettings = initialState.publishSettings
    },
    clearUpdateError: (state) => {
      state.updateError = null
      state.updateStatus = "idle"
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createCampaign.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.campaigns.push(action.payload)
        state.currentCampaign = action.payload
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to create campaign"
      })
      .addCase(updatePost.pending, (state) => {
        state.updateStatus = "loading"
        state.updateError = null
      })
      .addCase(updatePost.fulfilled, (state, action) => {
        state.updateStatus = "succeeded"
        state.updateError = null
      })
      .addCase(updatePost.rejected, (state, action) => {
        state.updateStatus = "failed"
        state.updateError = action.payload || "Failed to update post"
      })
  },
})

export const {
  clearCampaigns,
  clearCampaignError,
  setCurrentCampaign,
  updatePublishSettings,
  resetCampaignOnLogout,
  clearUpdateError,
} = campaignSlice.actions

export default campaignSlice.reducer
